import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ControlContainer, FormGroupDirective, UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { ExtensionPrivileges } from 'src/app/pages/extension-details/models/extension-privileges';
import { GetExtensionDetail_extension_mailbox } from 'src/app/pages/extension-details/queries/generated/GetExtensionDetail';
import { MailboxMessagesRegisterComponent } from 'src/app/pages/mailbox-messages/mailbox-messages-register.component';
import { PromptPrivileges } from 'src/app/pages/prompt-register/models/prompt-privileges';
import { CurrentlyViewingMailboxMessagesRegisterHub } from 'src/app/signalr/currently-viewing-mailbox-message-register-hub/currently-viewing-mailbox-message-register-hub.service';
import { promptItemDialogSubmit as extensionPromptItemDialogSubmit } from '../../../extension-details/state/extension-detail.actions';
import { VoiceMailGreetingPromptOption } from '../../models/voicemail-greeting-prompt-option.model';
import {
   editMailboxPinSubmit,
   promptItemDialogSubmit as mailboxPromptItemDialogSubmit,
} from '../../state/mailbox-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-mailbox-detail',
    styleUrls: ['./mailbox-detail-view.component.scss'],
    templateUrl: './mailbox-detail-view.component.html',
    standalone: false
})
export class MailboxDetailComponent extends Detail<FormsBase> implements OnInit, OnDestroy, DoCheck {
   @Input() callState: CallState;

   @Input() set mailbox(value: GetExtensionDetail_extension_mailbox | null) {
      this._mailbox = value;
      this.hiddenTemporaryGreetingPrompt = null;
      this.disableReset =
         this.mailbox?.password == null &&
         this.mailbox?.busyPrompt == null &&
         this.mailbox?.namePrompt == null &&
         this.mailbox?.unavailablePrompt == null &&
         this.mailbox?.temporaryUnavailablePrompt == null;
      if (value) {
         this.currentlyViewingMailboxMessagesRegisterHub.registerAsViewingMailboxMessagesRegister(
            value.mailboxId ?? null
         );
      }
   }

   get mailbox(): GetExtensionDetail_extension_mailbox | null {
      return this._mailbox;
   }

   get isSmartVoicemail(): boolean {
      return this.extensionType === 'SmartVoicemail';
   }

   private _mailbox: GetExtensionDetail_extension_mailbox | null;

   private hiddenTemporaryGreetingPrompt: { promptId?: number | null } | null = null;

   @Input() extensionPrivileges: ExtensionPrivileges;

   @Input() promptPrivileges: PromptPrivileges;

   @Input() accountId: number | null;

   @Input() AccountOperatorExtension: string;

   @Input() extensionType: string | null;

   @Output() addNewPromptClick = new EventEmitter<VoiceMailGreetingPromptOption | null>();

   @Output() unreadMessageCountChanged = new EventEmitter<number>();

   @ViewChild(MailboxMessagesRegisterComponent)
   private mailboxRegister: MailboxMessagesRegisterComponent;

   public getMailboxMessageIds(): number[] {
      return this.mailboxRegister?.getMailboxMessageIds() ?? [];
   }

   public mailboxHasPrompts(): boolean {
      return (
         this.mailbox?.busyPromptId ||
         this.mailbox?.unavailablePromptId2 ||
         this.mailbox?.namePromptId ||
         this.mailbox?.temporaryUnavailablePromptId
      );
   }

   public getMailboxId(): number {
      return this.mailboxRegister?.mailboxId;
   }

   form: UntypedFormGroup;

   namePromptsToInclude: number[] = [];

   unavailablePromptsToInclude: number[] = [];

   busyPromptsToInclude: number[] = [];

   temporaryUnavailablePromptsToInclude: number[] = [];

   editPasswordEnabled = false;

   disableReset: boolean = true;

   constructor(
      public store: Store,
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      private parentForm: FormGroupDirective,
      private actions$: Actions,
      private currentlyViewingMailboxMessagesRegisterHub: CurrentlyViewingMailboxMessagesRegisterHub
   ) {
      super(store);
   }

   ngOnDestroy(): void {
      this.currentlyViewingMailboxMessagesRegisterHub.unregisterAsViewingMailboxMessagesRegister(
         this.mailbox?.mailboxId ?? null
      );
      this.destroyed$.next();
      this.mailbox = null;
   }

   ngOnInit(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;
      if (!this.extensionPrivileges?.canEditOthers) {
         this.form.disable();
      }
      this.actions$
         .pipe(ofType(extensionPromptItemDialogSubmit, mailboxPromptItemDialogSubmit), takeUntil(this.destroyed$))
         .subscribe((action) => {
            switch (action.promptItem.voiceMailGreetingPrompt) {
               case VoiceMailGreetingPromptOption.Name:
                  this.namePromptsToInclude.push(action.promptItem.promptId);
                  this.form.controls['namePrompt'].setValue(action.promptItem);
                  this.form.controls['namePrompt'].markAsDirty();
                  this.form.controls['namePrompt'].updateValueAndValidity();
                  break;
               case VoiceMailGreetingPromptOption.Unavailable:
                  this.unavailablePromptsToInclude.push(action.promptItem.promptId);
                  this.form.controls['unavailablePrompt'].setValue(action.promptItem);
                  this.form.controls['unavailablePrompt'].markAsDirty();
                  this.form.controls['unavailablePrompt'].updateValueAndValidity();
                  break;
               case VoiceMailGreetingPromptOption.Busy:
                  this.busyPromptsToInclude.push(action.promptItem.promptId);
                  this.form.controls['busyPrompt'].setValue(action.promptItem);
                  this.form.controls['busyPrompt'].markAsDirty();
                  this.form.controls['busyPrompt'].updateValueAndValidity();
                  break;
               case VoiceMailGreetingPromptOption.Temporary:
                  this.temporaryUnavailablePromptsToInclude.push(action.promptItem.promptId);
                  this.form.controls['useTemporaryGreeting'].setValue(true);
                  this.form.controls['temporaryUnavailablePrompt'].setValue(action.promptItem);
                  this.form.controls['temporaryUnavailablePrompt'].markAsDirty();
                  this.form.controls['temporaryUnavailablePrompt'].updateValueAndValidity();
                  break;
            }
            this.changeDetectorRef.markForCheck();
         });

      this.actions$.pipe(ofType(editMailboxPinSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         this.form.controls['password'].setValue(action.password);
         this.form.controls['password'].markAsDirty();
         this.form.controls['password'].updateValueAndValidity();
         if (this.parentForm.form.controls['mailboxEditPin']) {
            this.parentForm.form.controls['mailboxEditPin'].get('password')?.setValue(action.password);
            this.parentForm.form.controls['mailboxEditPin'].get('passwordConfirm')?.setValue(action.password);
         }
         this.changeDetectorRef.markForCheck();
      });
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && (this.parentForm.submitted || this.form.touched)) {
         this.form.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }

   onAddNamePrompt(): void {
      this.addNewPromptClick.emit(VoiceMailGreetingPromptOption.Name);
   }

   onAddUnavailablePrompt(): void {
      this.addNewPromptClick.emit(VoiceMailGreetingPromptOption.Unavailable);
   }

   onAddBusyPrompt(): void {
      this.addNewPromptClick.emit(VoiceMailGreetingPromptOption.Busy);
   }

   onAddTemporaryUnavailablePrompt(): void {
      this.addNewPromptClick.emit(VoiceMailGreetingPromptOption.Temporary);
   }

   onUseTemporaryGreetingChange(event: MatCheckboxChange): void {
      this.form.controls['useTemporaryGreeting'].setValue(event.checked);
      this.form.controls['useTemporaryGreeting'].markAsPristine();
      const temporaryPromptControl = this.form.controls['temporaryUnavailablePrompt'];

      if (event.checked) {
         if (this.hiddenTemporaryGreetingPrompt) {
            temporaryPromptControl.setValue(this.hiddenTemporaryGreetingPrompt);
            this.updateTemporaryGreetingPromptDirtyState();
            temporaryPromptControl.updateValueAndValidity();
         }
         this.hiddenTemporaryGreetingPrompt = null;
         this.changeDetectorRef.markForCheck();
         return;
      }

      if (!event.checked) {
         this.hiddenTemporaryGreetingPrompt = temporaryPromptControl.value ?? this.hiddenTemporaryGreetingPrompt;
         temporaryPromptControl.setValue(null);
         this.updateTemporaryGreetingPromptDirtyState();
         temporaryPromptControl.updateValueAndValidity();
      }
      this.changeDetectorRef.markForCheck();
   }

   isTemporaryGreetingEnabled(): boolean {
      return !!this.form?.controls['useTemporaryGreeting']?.value;
   }

   private updateTemporaryGreetingPromptDirtyState(): void {
      const temporaryPromptControl = this.form.controls['temporaryUnavailablePrompt'];
      const originalTemporaryPromptId =
         this.mailbox?.temporaryUnavailablePrompt?.promptId ?? this.mailbox?.temporaryUnavailablePromptId ?? null;
      const currentTemporaryPromptId = temporaryPromptControl.value?.promptId ?? null;

      if (currentTemporaryPromptId === originalTemporaryPromptId) {
         temporaryPromptControl.markAsPristine();
      } else {
         temporaryPromptControl.markAsDirty();
      }
   }

   canAddPrompts(): boolean {
      return this.promptPrivileges.canAdd && this.extensionPrivileges.canEditOthers;
   }
}
